import { ref, type Ref } from 'vue';

import { onScopeDisposeOrWarn } from '@platform/vue/on-scope-dispose-or-warn';

/**
 * Tracks which feature gates the loaded application has actually resolved this session
 * (every {@link useFeatureGate} call marks its id). The DevTools panel reads this to
 * emphasise gates "in use here" and collapse the rest.
 *
 * Window-anchored, exactly like the provider registry: @abax/platform is NOT a Module
 * Federation singleton, so the host (where the DevTools panel runs) and each MFE evaluate
 * their own copy of this module. A module-local set would leave gates an MFE checked
 * invisible to the panel. The window anchor gives every copy one shared set. Falls back to
 * a module-local registry where `window` is absent (SSR, Node test env).
 */
interface UsedGatesRegistry {
  gates: Set<string>;
  subscribers: Set<() => void>;
}

const USED_GATES_KEY = '__ABAX_USED_FEATURE_GATES__';
let fallbackRegistry: UsedGatesRegistry | undefined;

function createRegistry(): UsedGatesRegistry {
  return { gates: new Set(), subscribers: new Set() };
}

function getRegistry(): UsedGatesRegistry {
  if (typeof window === 'undefined') {
    fallbackRegistry ??= createRegistry();
    return fallbackRegistry;
  }
  const w = window as unknown as Record<string, UsedGatesRegistry | undefined>;
  w[USED_GATES_KEY] ??= createRegistry();
  return w[USED_GATES_KEY] as UsedGatesRegistry;
}

/**
 * Record that a gate was resolved by the loaded app. Idempotent — a gate already known
 * is a no-op (no churn, no subscriber notification). Notifies subscribers only on the
 * first sighting of an id.
 */
export function markGateUsed(id: string): void {
  const registry = getRegistry();
  if (registry.gates.has(id)) return;
  registry.gates.add(id);
  for (const cb of registry.subscribers) {
    try {
      cb();
    } catch {
      // A faulty subscriber must not break gate resolution for the rest.
    }
  }
}

/** Snapshot of the gate ids resolved so far this session. */
export function getUsedGates(): string[] {
  return Array.from(getRegistry().gates);
}

/** Whether a gate has been resolved by the loaded app this session. */
export function isGateUsed(id: string): boolean {
  return getRegistry().gates.has(id);
}

/**
 * Subscribe to first-sighting of a newly-used gate. Returns an unsubscribe function.
 * Window-anchored like the set, so a gate an MFE marks is observable from any copy.
 */
export function onUsedGatesChange(cb: () => void): () => void {
  const { subscribers } = getRegistry();
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

export interface UseUsedFeatureGatesResult {
  /** Reactive set of gate ids the loaded app has resolved this session. */
  usedGates: Ref<ReadonlySet<string>>;
}

/**
 * Read the "used gates" set reactively. The ref re-emits a fresh set whenever a new gate
 * is first resolved anywhere in the process, so the DevTools panel re-groups live as the
 * loaded MFE checks gates. Auto-unsubscribes on scope dispose.
 */
export function useUsedFeatureGates(): UseUsedFeatureGatesResult {
  const usedGates = ref<ReadonlySet<string>>(new Set(getUsedGates()));
  const unsubscribe = onUsedGatesChange(() => {
    usedGates.value = new Set(getUsedGates());
  });
  onScopeDisposeOrWarn(unsubscribe, 'useUsedFeatureGates()');
  return { usedGates };
}

/** @internal Test/HMR helper — clears the used-gate set and subscribers. */
export function __resetUsedFeatureGates(): void {
  const registry = getRegistry();
  registry.gates.clear();
  registry.subscribers.clear();
}
